import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShipyardService } from '@service/shipyard.service';
import { VesselService } from '@service/vessel.service';
import { Subject, Subscription, lastValueFrom, take, takeUntil } from 'rxjs';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { NewVesselDialogComponent } from '@components/new-vessel-dialog/new-vessel-dialog.component';
import { SidebarService } from '@service/sidebar.service';
import { VesselSocketService } from '@service/vessel-socket.service';
import { MatDialog } from '@angular/material/dialog';
import { LocationStrategy } from '@angular/common';
import { VesselInfoContainerComponent } from './vessel-info-container/vessel-info-container.component';
import { DeleteConfirmationDialogComponent } from '@components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { AuthService } from '@service/auth.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { AccessRequestSocketService } from '@service/access-request-socket.service';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { Shipyard } from 'icreate/common/models/shipyard';
import { MessageDialogService } from '@service/message-dialog.service';
import { PublishService } from '@service/publish.service';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { VesselOwnershipService } from '@service/vessel-ownership.service';
import { VesselOwnershipStatus, VesselOwnershipTransfer } from 'icreate/common/models/vessel-ownership-transfer';
import { UserCompanyService } from '@service/user-company.service';
import { VesselOwnershipSocketService } from '@service/vessel-ownership-socket.service';

@Component({
    selector: 'app-vessel-page',
    templateUrl: './vessel-page.component.html',
    styleUrls: ['./vessel-page.component.css'],
    standalone: false,
})
export class VesselPageComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('VesselInfo') VesselInfo: VesselInfoContainerComponent;
    @ViewChild('removeRequestConfirmation') removeRequestConfirmation: ConfirmationWindowComponent;

    selectedVessel: Vessel;
    selectedShipyard: Shipyard;
    shipyardiApi: string;
    versionToApprove: string;

    parameterSubscription: Subscription;

    shouldBlurPage: boolean = false;
    showOtherOptions: boolean = false;
    isLoading: boolean = false;
    hasPreviousVersion: boolean = false;

    accessRequestId: string;

    destroySubscriptions$: Subject<void> = new Subject();

    urlState: any;
    stateChecked: boolean = false;

    private vesselNames: { [key: string]: string[] };
    shipyardList: Shipyard[];
    vesselsEditors: any[];
    currentUser: { name: string; oid: string; email: string };
    approversList: any;
    ownershipTransfers: VesselOwnershipTransfer[];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private vesselService: VesselService,
        private shipyardService: ShipyardService,
        private sidebarService: SidebarService,
        private vesselSocketService: VesselSocketService,
        private dialog: MatDialog,
        private locationStrategy: LocationStrategy,
        private authService: AuthService,
        private accessRequestSocketService: AccessRequestSocketService,
        private messageDialogService: MessageDialogService,
        private publishService: PublishService,
        private ownershipService: VesselOwnershipService,
        private userCompanyService: UserCompanyService,
        private vesselOwnershipSocketService: VesselOwnershipSocketService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.otherOptions?.nativeElement.contains(event.target) && this.showOtherOptions) {
            this.showOtherOptions = false;
        }
    }

    async ngOnInit() {
        this.currentUser = this.authService.getCurrentUser();
        this.approversList = await this.userCompanyService.getUsersCompany(this.currentUser.oid);

        this.route.params.pipe(takeUntil(this.destroySubscriptions$)).subscribe(async params => {
            this.accessRequestId = null;
            const shipyardId = params['shipyardId'];
            const vesselId = params['vesselId'];
            this.shipyardList = await this.shipyardService.getList();
            this.selectedShipyard = this.shipyardList?.find(s => s.id === shipyardId);
            if (this.selectedShipyard) {
                this.shipyardiApi = this.selectedShipyard.iApiCompanyId;
            }

            const currentVessel: any = await this.vesselService.getVessel(vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }
            this.vesselService.setActiveVessel(currentVessel);
            this.selectedVessel = currentVessel;

            this.versionToApprove = this.selectedVessel.projectVersion;

            this.vesselNames = this.shipyardList.reduce((acc, { id, vessels }) => {
                acc[id] = vessels.map(vessel => vessel.name);
                return acc;
            }, {});

            this.sidebarService.updateSelectedVessel(this.selectedVessel.shipyardId, this.selectedVessel.id);

            const { hasPreviousVersion } = await this.vesselService.hasPreviousVersion(
                this.selectedVessel.id,
                this.selectedVessel.iAPIVesselId,
                this.shipyardiApi
            );
            this.hasPreviousVersion = hasPreviousVersion;

            this.ownershipTransfers = await this.ownershipService.getOwnershipTransfers(vesselId);
        });

        this.shipyardService
            .listUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async () => {
                this.shipyardList = this.shipyardService.getUpdatedList();
                this.vesselNames = this.shipyardList.reduce((acc, { id, vessels }) => {
                    acc[id] = vessels.map(vessel => vessel.name);
                    return acc;
                }, {});
            });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (!this.selectedVessel) return;

                if (this.selectedVessel.id === updatedVessel.id) {
                    this.selectedVessel = updatedVessel;
                }
                await this.vesselService.updateVesselInList(updatedVessel);
            });

        this.vesselSocketService
            .vesselDeleted$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async ({ vesselId, shipyardId }) => {
                if (!this.selectedVessel) return;

                if (this.selectedVessel.id === vesselId) {
                    this.router.navigateByUrl(`home/${shipyardId}`);
                }
            });

        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((accessRequest: AccessRequest) => {
                if (!this.selectedVessel) return;

                if (accessRequest.vesselId !== this.selectedVessel.id) return;
                const currentUser = this.authService.getCurrentUser();
                if (accessRequest.userOid == currentUser.oid) this.handleGuestStartEditing(accessRequest.id);
            });

        this.vesselOwnershipSocketService.ownershipTransfers$
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(list => {
                this.ownershipTransfers = list;
            });
    }

    async ngAfterViewChecked() {
        if (!this.selectedVessel || !this.VesselInfo || this.stateChecked) {
            return;
        }
        this.stateChecked = true;
        this.router.events.pipe(take(1)).subscribe(event => {
            this.urlState = this.locationStrategy.getState();
            this.locationStrategy.replaceState(
                'urlState',
                '',
                `home/${this.selectedVessel.shipyardId}/${this.selectedVessel.id}`,
                ''
            );
            if (this.urlState?.origin) {
                this.VesselInfo.startNewVersion(true);
            }
        });
    }

    ngOnDestroy(): void {
        this.vesselService.setActiveVessel(null);
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    handleProjectStatusChanged(udpatedStatus: ProjectStatus) {
        this.selectedVessel.projectStatus = udpatedStatus;
        if (udpatedStatus === ProjectStatus.undefined) {
            this.selectedVessel.ownerName = null;
            this.selectedVessel.ownerOid = null;
            this.selectedVessel.locked = false;
        }
    }

    async editVessel(skipPermissions = false) {
        if (!skipPermissions && !this.editVesselIsEnabled()) {
            this.dialog.open(PopUpNotificationComponent, {
                width: '474px',
                data: {
                    title: 'general.api.permission-denied.title',
                    message: 'general.api.permission-denied.message',
                    message2: 'general.api.permission-denied.message-2',
                },
            });
            return;
        }
        const currentUser = this.authService.getCurrentUser();
        let isOwner = false;
        if (currentUser.oid == this.selectedVessel.ownerOid) isOwner = true;

        const dialogRef = this.dialog.open(NewVesselDialogComponent, {
            width: '474px',
            data: {
                selectedShipyardId: this.selectedVessel.shipyardId,
                vesselData: this.selectedVessel,
                isOwner,
                skipPermissions,
                shipyardList: this.shipyardList,
            },
        });
        const vesselData = await lastValueFrom(dialogRef.afterClosed());
        if (vesselData) {
            this.isLoading = true;
            try {
                const { name, number, numberOps, numberFcu, drawingId } = vesselData;
                await this.vesselService.update(vesselData, this.selectedVessel);
                this.selectedVessel.name = name;
                this.selectedVessel.number = number;
                this.selectedVessel.drawingId = drawingId;
                this.selectedVessel.numberOps = numberOps;
                this.selectedVessel.numberFcu = numberFcu;
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    canWithdrawApproval(): boolean {
        if (this.selectedVessel.projectStatus !== ProjectStatus.PendingApproval) return false;
        const currentUser = this.authService.getCurrentUser();
        return currentUser.oid == this.selectedVessel.ownerOid;
    }

    async deleteVessel() {
        if (!this.deleteVesselIsEnabled()) {
            this.dialog.open(PopUpNotificationComponent, {
                width: '474px',
                data: {
                    title: 'general.api.permission-denied.title',
                    message: 'general.api.permission-denied.message',
                    message2: 'general.api.permission-denied.message-2',
                },
            });
            return;
        }
        const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
            width: '474px',
            data: {
                title: 'vessel.delete.dialog.title',
                name: this.selectedVessel.name,
                message1: 'vessel.delete.dialog.message.1',
                message2: 'vessel.delete.dialog.message.2',
                cancelButton: 'vessel.delete.dialog.button.cancel',
                buttonDelete: 'vessel.delete.dialog.button.delete',
                inputLegend: 'vessel.delete.dialog.input.legend',
            },
        });
        const shouldDelete = await lastValueFrom(dialogRef.afterClosed());

        if (shouldDelete) {
            this.isLoading = true;
            try {
                await this.vesselService.deleteVessel(
                    this.selectedVessel.id,
                    this.selectedVessel.shipyardId,
                    this.selectedVessel.iAPIVesselId,
                    this.shipyardiApi
                );
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
            this.router.navigateByUrl(`home/${this.selectedVessel.shipyardId}`);
        }
    }

    async duplicateVessel() {
        const currentUser = this.authService.getCurrentUser();
        let isOwner = false;
        if (currentUser.oid == this.selectedVessel.ownerOid) isOwner = true;

        const newVesselData = structuredClone(this.selectedVessel);
        newVesselData.name = this.selectedVessel.name;
        newVesselData.number = this.selectedVessel.number + '_' + 1;
        newVesselData.drawingId = this.selectedVessel.drawingId;

        const dialogRef = this.dialog.open(NewVesselDialogComponent, {
            width: '474px',
            data: {
                selectedShipyardId: this.selectedVessel.shipyardId,
                vesselData: newVesselData,
                isOwner,
                isDuplicate: true,
                shipyardList: this.shipyardList,
            },
        });
        const vesselData = await lastValueFrom(dialogRef.afterClosed());
        if (!vesselData) return;

        try {
            this.isLoading = true;
            const userName = this.authService.getCurrentUser().name;
            vesselData.userName = userName;
            vesselData.shipyardId = this.selectedVessel.shipyardId;
            await this.vesselService.duplicate(vesselData, this.selectedVessel.id);

            this.isLoading = false;
            this.messageDialogService.openMessage('recent.vessel.create.notification.title', {
                number: vesselData.number,
                name: vesselData.name,
            });
        } catch (err) {
            console.error(err);
            this.isLoading = false;
        }
    }

    private generateNextAvailable(baseName: string, taken: Set<string>): string {
        let n = 1;
        const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const rx = new RegExp(`^${escapedBase}_copy_(\\d+)$`);

        let maxN = 0;
        for (const name of taken) {
            const m = name.match(rx);
            if (m) {
                maxN = Math.max(maxN, parseInt(m[1], 10));
            }
        }

        n = maxN + 1;
        let candidate = `${baseName}_copy_${n}`;
        while (taken.has(candidate)) {
            n++;
            candidate = `${baseName}_copy_${n}`;
        }

        return candidate;
    }

    handleGuestStartEditing(requestId) {
        this.shouldBlurPage = false;
        this.accessRequestId = requestId;
    }

    editVesselIsEnabled(): boolean {
        if (this.selectedVessel.projectStatus != ProjectStatus.Editing) return false;
        if (this.selectedVessel.projectStatus == ProjectStatus.Editing) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser.oid == this.selectedVessel.ownerOid) {
                return true;
            } else if (this.accessRequestId) {
                return true;
            } else {
                return this.selectedVessel.VesselAccessRequests?.some(
                    request =>
                        request.userOid === currentUser.oid && request.requestStatus === AccessRequestStatus.Editing
                );
            }
        }
        return false;
    }

    deleteVesselIsEnabled(): boolean {
        if (this.selectedVessel.projectStatus == ProjectStatus.undefined) return true;
        if (this.selectedVessel.projectStatus == ProjectStatus.Editing) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser.oid == this.selectedVessel.ownerOid) {
                return true;
            }
        }
        return false;
    }

    handleEditorLeave(accessRequest: AccessRequest) {
        if (this.selectedVessel.VesselAccessRequests?.length == 0) return;
        this.selectedVessel.VesselAccessRequests.map(request => {
            if (request.id == accessRequest.id) {
                request.requestStatus = AccessRequestStatus.Finished;
                request.isEditing = false;
            }
            if (accessRequest.id == this.accessRequestId) this.accessRequestId = null;
            return request;
        });
    }

    async handleTransferUpdate(requestedData) {
        this.isLoading = true;
        try {
            const payload = this.getReleaseOwnershipPayload(requestedData);
            await this.ownershipService.updateOwnershipTransferStatus(requestedData.id, payload);
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async handleOwnerChange(ownershipData: OwnerChangeEvent) {
        this.isLoading = true;
        try {
            const payload = this.getReleaseOwnershipPayload(ownershipData);
            await this.ownershipService.changeVesselOwnership(payload);
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    getReleaseOwnershipPayload(ownershipData?) {
        return {
            shipyardId: this.selectedVessel?.shipyardId,
            vesselId: this.selectedVessel?.id,
            vesselName: this.selectedVessel?.number + ' - ' + this.selectedVessel?.name,
            senderName: this.currentUser?.name,
            senderEmail: this.currentUser?.email,
            senderOid: this.currentUser?.oid,
            receiverName: ownershipData?.selectedOwner?.name ?? this.currentUser?.name,
            receiverEmail:
                ownershipData?.selectedOwner?.email ??
                this.getreceiverEmail(ownershipData?.selectedOwner?.oid) ??
                this.currentUser?.email,
            receiverOid: ownershipData?.selectedOwner?.oid ?? this.currentUser?.oid,
            assignerOid: this.currentUser?.oid,
            comment: ownershipData?.comment,
            vesselVersion: this.selectedVessel?.projectVersion,
            ownershipStatus: ownershipData.status,
        };
    }

    getreceiverEmail(userOid: string) {
        if (!userOid) return;
        const user = this.approversList.find(user => user.oid === userOid);
        return user.email;
    }

    goToShipyardPage() {
        this.router.navigateByUrl(`home/${this.selectedShipyard.id}`);
    }

    async withdrawApprovalRequest() {
        if (!this.canWithdrawApproval()) return;
        if (this.selectedVessel.projectStatus !== ProjectStatus.PendingApproval) return;

        this.removeRequestConfirmation.setVisible(true);
        const shouldRemove = await lastValueFrom(this.removeRequestConfirmation.onOptionSelected.pipe(take(1)));
        if (shouldRemove !== ConfirmationWindowTypes.SAVE) return;

        try {
            this.isLoading = true;
            this.publishService.removeRequest(
                this.selectedVessel.id,
                this.selectedVessel.iAPIVesselId,
                this.selectedShipyard.iApiCompanyId,
                this.selectedVessel.projectVersion
            );
        } catch (err) {
            console.log(err);
        } finally {
            this.isLoading = false;
        }
    }

    get isPendingApproval() {
        return this.selectedVessel.projectStatus === ProjectStatus.PendingApproval;
    }

    get projectIcon() {
        if (this.selectedVessel.projectStatus === ProjectStatus.Approved) return 'spaceship';
        else if (this.isPendingApproval) return 'pending-approval';
        else if (this.selectedVessel.ownerOid) return 'lock-profile';
        return 'unlocked';
    }

    get hasIconBackground() {
        return this.projectIcon == 'spaceship' || this.projectIcon == 'pending-approval';
    }

    get vesselStatus() {
        if (this.selectedVessel.projectStatus === ProjectStatus.Approved) return 'vessel.page.status.approved';
        else if (this.isPendingApproval) return 'vessel.page.status.review';
        else if (this.selectedVessel.ownerOid) return 'vessel.page.status.editing';
        return 'vessel.page.status.unassigned';
    }
}

export interface OwnerChangeEvent {
    selectedOwner: { name: string; oid: string };
    comment?: string;
    status: VesselOwnershipStatus;
}
